import { gql, useMutation } from "@apollo/client";
import { alpha, ButtonBase, CircularProgress, darken } from "@mui/material";
import apolloClient from "helpers/apolloClient";
import { COLOR_BLACK, COLOR_WHITE_LIGHTER } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { SPACING_HALF } from "helpers/spacings";
import useActionFragment from "helpers/useActionFragment";
import useShowMessage from "helpers/useShowMessage";
import { CheckCircleOutline, Creation } from "mdi-material-ui";
import React, { useEffect, useRef, useState } from "react";

import GameOverviewAiStatsDialog, { COLOR_AI_ACTION } from "./GameOverviewAiStatsDialog";

export const GameOverviewAiStatsFragment = gql`
  fragment GameOverviewAiStatsFragment on Game {
    id
    aiStatsAvailable
    aiStatsStatus
    aiStatsRequestedByCurrentUser
  }
`;

// Plain useMutation rather than useAction: this runs unasked, so a failure must not toast.
const AI_STATS_REQUEST_REFRESH = gql`
  mutation ($input: AiStatsRequestRefreshInput!) {
    aiStatsRequestRefresh(input: $input) {
      game {
        ...GameOverviewAiStatsFragment
      }
    }
  }
  ${GameOverviewAiStatsFragment}
`;

// Yellow -> invites, orange -> working in progress, green -> done.
const COLOR_AI_PROCESSING = "#EF8B26";
const COLOR_AI_DONE = "#35A05B";

// A single pill between the score and the video, saying where this game is up
// to. It replaces the button-plus-explainer split: one control, one line, and
// the same shape at every viewport width.
export default function GameOverviewAiStats({ game }) {
  const [submitting, submittingSet] = useState(false);
  const aiStatsRequestCreate = useActionFragment("aiStatsRequestCreate", "redirectUrl game { id aiStatsStatus }");
  const showMessage = useShowMessage();
  const [confirmOpen, confirmOpenSet] = useState(false);
  const [refreshAiStats] = useMutation(AI_STATS_REQUEST_REFRESH, { client: apolloClient });
  const [checking, checkingSet] = useState(false);
  const [restoredCount, restoredCountSet] = useState(0);
  const checkedGameId = useRef(null);

  const status = game?.aiStatsStatus;

  // A user back from buying credits on Preciser still reads as no_credits until the video.processing
  // webhook lands, so ask the provider once before offering a retry.
  useEffect(() => {
    if (status !== "failed" || checkedGameId.current === game.id) return;

    checkedGameId.current = game.id;
    checkingSet(true);
    refreshAiStats({ variables: { input: { gameId: game.id } } })
      .catch(() => {}) // The retry stays on offer.
      .finally(() => checkingSet(false));
  }, [status, game?.id, restoredCount]);

  // Coming back with the browser's back button restores this page from the
  // bfcache with its React state intact, so the pill would still be sitting in
  // "Taking you to Preciser…" for a navigation that is over. `pageshow` fires on
  // a bfcache restore where `load` does not, so it is the only reliable signal.
  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) {
        submittingSet(false);
        checkedGameId.current = null;
        restoredCountSet((count) => count + 1);
      }
    };

    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  if (!game || status === "unavailable") {
    return null;
  }

  const onClick = () => confirmOpenSet(true);

  // Same tab rather than a popup: most users are on mobile, and in the app web
  // views some leagues embed us in, window.open silently does nothing.
  const onConfirm = async () => {
    confirmOpenSet(false);
    submittingSet(true);
    showMessage("Taking you to Preciser…");
    try {
      const result = await aiStatsRequestCreate({
        input: { gameId: game.id, returnUrl: window.location.href },
      });
      // useAction hands back the whole mutation payload, so the fields are one
      // level down under the mutation name.
      const url = result?.aiStatsRequestCreate?.redirectUrl;
      if (!url) throw new Error("No redirect URL returned");

      // Left deliberately submitting: the navigation is about to replace the
      // page, and flipping the pill back to its idle state first reads as if
      // nothing happened.
      window.location.assign(url);
    } catch (e) {
      // Stay put after a failed submit — the provider's page would show a
      // purchase screen for a request that never landed.
      //
      // useAction has already shown the real GraphQL error and then thrown an
      // AbortError purely to unwind us. Showing that too would put a second
      // toast reading "Cancelled" on top of the actual reason.
      if (!e?.isAbortError) {
        showMessage(e?.message || "Could not start AI stats. Please try again.");
      }
      submittingSet(false);
    }
  };

  const pill = pillFor({ status, submitting, checking });

  return (
    <div style={{ display: "flex", justifyContent: "center", marginTop: SPACING_HALF, width: "100%" }}>
      <GameOverviewAiStatsDialog open={confirmOpen} onClose={() => confirmOpenSet(false)} onConfirm={onConfirm} />
      <ButtonBase
        onClick={pill.action ? onClick : undefined}
        disabled={!pill.action || submitting}
        disableRipple={!pill.action}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "9px 22px",
          borderRadius: "999px",
          backgroundColor: pill.background,
          color: pill.color,
          fontFamily: FONT_FAMILY_GRAPHIK,
          fontSize: "0.875rem",
          fontWeight: 400,
          lineHeight: 1.2,
          textAlign: "center",
          maxWidth: "100%",
          // Only the actionable state glows. A status has nothing to invite.
          boxShadow: pill.action ? `0 0 2px ${alpha(pill.background, 0.5)}` : "none",
          transition: "background-color 120ms ease, box-shadow 120ms ease, transform 120ms ease",
          // A status pill is not a control, so it must not read as disabled —
          // it keeps its colour and its default cursor.
          "&.Mui-disabled": { color: pill.color, opacity: 1 },
          // Hover belongs only to the pill you can actually press. Putting it on
          // the status pills would advertise a click that does nothing.
          ...(pill.action && {
            "&:hover": {
              backgroundColor: darken(pill.background, 0.08),
              boxShadow: `0 0 3px ${alpha(pill.background, 0.9)}`,
              transform: "translateY(-1px)",
            },
            "&:active": { transform: "translateY(0)" },
          }),
        }}
      >
        {pill.icon}
        <span>{pill.text}</span>
      </ButtonBase>
    </div>
  );
}

function pillFor({ status, submitting, checking }) {
  if (submitting) {
    return {
      background: COLOR_AI_PROCESSING,
      color: COLOR_WHITE_LIGHTER,
      icon: <CircularProgress size={16} thickness={5} style={{ color: COLOR_WHITE_LIGHTER }} />,
      text: "Taking you to Preciser…",
      action: false,
    };
  }

  if (checking && status === "failed") {
    return {
      background: COLOR_AI_PROCESSING,
      color: COLOR_WHITE_LIGHTER,
      icon: <CircularProgress size={16} thickness={5} style={{ color: COLOR_WHITE_LIGHTER }} />,
      text: "Checking AI Stats…",
      action: false,
    };
  }

  // Already imported. Everyone sees it, and it is what stops a second person
  // paying for a game that has already been through AI.
  if (status === "completed") {
    return {
      background: COLOR_AI_DONE,
      color: COLOR_WHITE_LIGHTER,
      icon: <CheckCircleOutline style={{ fontSize: 18 }} />,
      text: "Game Includes AI Stats",
      action: false,
    };
  }

  // Waiting on the requester to finish the provider's form. Actionable, so it
  // keeps the inviting colour rather than the working one.
  if (status === "info_required") {
    return {
      background: COLOR_AI_ACTION,
      color: COLOR_BLACK,
      icon: <Creation style={{ fontSize: 18 }} />,
      text: "Finish AI Stats Setup",
      action: true,
    };
  }

  // The requester's last attempt did not land — our submit errored, or they were out of credits and
  // have just come back from the purchase screen. Per the partner guide §5 a top-up is not the end of
  // it: the video has to be submitted again from our side, and this is the control that does it.
  if (status === "failed") {
    return {
      background: COLOR_AI_ACTION,
      color: COLOR_BLACK,
      icon: <Creation style={{ fontSize: 18 }} />,
      text: "Retry AI Stats",
      action: true,
    };
  }

  if (status === "in_progress") {
    return {
      background: COLOR_AI_PROCESSING,
      color: COLOR_WHITE_LIGHTER,
      icon: <CircularProgress size={16} thickness={5} style={{ color: COLOR_WHITE_LIGHTER }} />,
      text: "AI Stats Processing",
      action: false,
    };
  }

  return {
    background: COLOR_AI_ACTION,
    color: COLOR_BLACK,
    icon: <Creation style={{ fontSize: 18 }} />,
    text: "Get AI Stats for this Game",
    action: true,
  };
}
